import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaTrash, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import './BookingManager.css';

const BookingManager = () => {
  const { t, language } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/bookings`);
      setBookings(response.data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      showMessage('error', t('admin.bookingLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // Уведомляем родительский компонент об обновлении счетчиков при загрузке
    window.dispatchEvent(new Event('admin:updateCounts'));
    
    // Обновляем бронирования каждые 15 секунд и уведомляем о новых
    const interval = setInterval(() => {
      fetchBookings();
      window.dispatchEvent(new Event('admin:updateCounts'));
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      setLoading(true);
      await axios.put(`${API_BASE_URL}/api/bookings/${id}`, { status });
      showMessage('success', t('admin.bookingStatusUpdated'));
      fetchBookings();
      // Уведомляем родительский компонент об обновлении счетчиков
      window.dispatchEvent(new Event('admin:updateCounts'));
    } catch (error) {
      console.error('Error updating booking:', error);
      showMessage('error', t('admin.bookingStatusUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.deleteBookingConfirm'))) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/bookings/${id}`);
      showMessage('success', t('admin.bookingDeleted'));
      fetchBookings();
      // Уведомляем родительский компонент об обновлении счетчиков
      window.dispatchEvent(new Event('admin:updateCounts'));
    } catch (error) {
      console.error('Error deleting booking:', error);
      showMessage('error', t('admin.bookingDeleteError'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      pending: { label: t('admin.statusPending'), color: '#ffa500' },
      confirmed: { label: t('admin.statusConfirmed'), color: '#4facfe' },
      cancelled: { label: t('admin.statusCancelled'), color: '#ff4d4d' }
    };
    const statusInfo = statuses[status] || statuses.pending;
    return (
      <span className="status-badge" style={{ backgroundColor: statusInfo.color }}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDateTime = (dateStr, timeStart, timeEnd) => {
    // Парсим дату из строки "YYYY-MM-DD" в локальное время
    // чтобы избежать проблем с timezone (new Date("YYYY-MM-DD") интерпретирует как UTC)
    let date;
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      // Создаем дату в ЛОКАЛЬНОМ времени
      date = new Date(year, month - 1, day, 0, 0, 0, 0);
    } else {
      date = new Date(dateStr);
    }
    
    const [hours, minutes] = (timeStart || '').split(':');
    if (hours && minutes) {
      date.setHours(parseInt(hours), parseInt(minutes));
    }
    
    const localeCode = language === 'en' ? 'en-US' : 'ru-RU';
    let formatted = date.toLocaleString(localeCode, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    if (timeStart && timeEnd) {
      formatted += ` - ${timeEnd}`;
    } else if (timeStart) {
      formatted += ` (${timeStart})`;
    }
    
    return formatted;
  };

  return (
    <motion.div
      className="booking-manager"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="booking-manager-header">
        <h2><FaCalendarAlt /> {t('admin.bookings')}</h2>
        <button onClick={fetchBookings} disabled={loading} className="refresh-btn">
          {loading ? <FaSpinner className="spinner" /> : t('admin.refresh')}
        </button>
      </div>

      {message.text && (
        <motion.div
          className={`alert ${message.type}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {message.text}
        </motion.div>
      )}

      {loading && bookings.length === 0 && (
        <div className="loading-container">
          <FaSpinner className="spinner" />
          <span>{t('admin.loadingBookings')}</span>
        </div>
      )}

      {!loading && bookings.length === 0 && (
        <div className="no-bookings">
          <FaCalendarAlt />
          <p>{t('admin.noBookings')}</p>
        </div>
      )}

      {bookings.length > 0 && (
        <div className="bookings-list">
          {bookings.map((booking) => (
            <motion.div
              key={booking.id}
              className="booking-item"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="booking-header">
                <div className="booking-date-time">
                  <FaCalendarAlt />
                  <span>{formatDateTime(booking.date, booking.timeStart || booking.time, booking.timeEnd)}</span>
                </div>
                <div className="booking-header-right">
                  {booking.project && (
                    <span className="project-badge">
                      {booking.project === 'underground' ? 'Underground' : 'Commercial'}
                    </span>
                  )}
                  {getStatusBadge(booking.status)}
                </div>
              </div>

              <div className="booking-details">
                <div className="detail-item">
                  <FaUser />
                  <span>{booking.name}</span>
                </div>
                <div className="detail-item">
                  <FaEnvelope />
                  <a href={`mailto:${booking.email}`}>{booking.email}</a>
                </div>
                {booking.phone && (
                  <div className="detail-item">
                    <FaPhone />
                    <a href={`tel:${booking.phone}`}>{booking.phone}</a>
                  </div>
                )}
                {booking.location && (
                  <div className="detail-item">
                    <FaMapMarkerAlt />
                    <span>{booking.location}</span>
                  </div>
                )}
                {booking.message && (
                  <div className="booking-message">
                    <p>{booking.message}</p>
                  </div>
                )}
              </div>

              <div className="booking-actions">
                {booking.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleStatusChange(booking.id, 'confirmed')}
                      disabled={loading}
                      className="action-btn confirm-btn"
                    >
                      <FaCheck /> {t('admin.confirm')}
                    </button>
                    <button
                      onClick={() => handleStatusChange(booking.id, 'cancelled')}
                      disabled={loading}
                      className="action-btn cancel-btn"
                    >
                      <FaTimes /> {t('admin.cancel')}
                    </button>
                  </>
                )}
                {booking.status === 'confirmed' && (
                  <button
                    onClick={() => handleStatusChange(booking.id, 'cancelled')}
                    disabled={loading}
                    className="action-btn cancel-btn"
                  >
                    <FaTimes /> {t('admin.cancel')}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(booking.id)}
                  disabled={loading}
                  className="action-btn delete-btn"
                >
                  <FaTrash /> {t('admin.delete')}
                </button>
              </div>

              <div className="booking-meta">
                <span>{t('admin.bookingCreated')}: {new Date(booking.createdAt).toLocaleString(language === 'en' ? 'en-US' : 'ru-RU')}</span>
                <span>ID: {booking.id}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default BookingManager;

